import { useState, useEffect } from "react";
import michaelImage from "../assets/michaelvanbeek-avatar.webp";
import type { JsonResume } from "../types/jsonResume";

interface ResumeData {
  basics: {
    name: string;
    label: string;
    image?: string;
    email?: string;
    linkedin?: string;
    github?: string;
    summary?: string;
  };
  work: Array<{
    name: string;
    position: string;
    startDate: string;
    endDate?: string;
    summary?: string;
  }>;
  education: Array<{
    institution: string;
    studyType: string;
    area: string;
    endDate?: string;
    courses?: string[];
  }>;
  skills: Array<{
    name: string;
    icon?: string;
  }>;
  interests: Array<{
    name: string;
    icon?: string;
  }>;
  projects: Array<{
    name: string;
    description?: string;
    url?: string;
    keywords?: string[];
  }>;
}

// Format date from ISO format to readable format
const formatDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${month} ${String(year)}`;
};

// Transform JSON Resume format to our internal format
const transformJsonResume = (jsonResume: JsonResume): ResumeData => {
  const linkedinProfile = jsonResume.basics.profiles?.find((p) => p.network === "LinkedIn");
  const githubProfile = jsonResume.basics.profiles?.find((p) => p.network === "GitHub");

  // Flatten skills from grouped format
  const flatSkills: Array<{ name: string }> = [];
  jsonResume.skills.forEach((skillGroup) => {
    if (skillGroup.keywords) {
      skillGroup.keywords.forEach((keyword) => {
        flatSkills.push({ name: keyword });
      });
    }
  });

  // Flatten interests
  const flatInterests: Array<{ name: string }> = [];
  jsonResume.interests.forEach((interestGroup) => {
    if (interestGroup.keywords) {
      interestGroup.keywords.forEach((keyword) => {
        flatInterests.push({ name: keyword });
      });
    }
  });

  return {
    basics: {
      name: jsonResume.basics.name,
      label: jsonResume.basics.label,
      image: (function () {
        const img = jsonResume.basics.image;
        if (!img) return undefined;
        if (img.startsWith("http") || img.startsWith("/")) return img;
        // Recognize known local filename and resolve to imported image
        if (img.toLowerCase().includes("michael")) return michaelImage;
        return img;
      })(),
      email: jsonResume.basics.email,
      linkedin: linkedinProfile?.url,
      github: githubProfile?.url,
      summary: jsonResume.basics.summary,
    },

    work: jsonResume.work.map((job) => ({
      name: job.name,
      position: job.position,
      startDate: formatDate(job.startDate),
      endDate: job.endDate ? formatDate(job.endDate) : "Present",
      summary: job.summary,
    })),
    education: jsonResume.education.map((edu) => ({
      institution: edu.institution,
      studyType: edu.studyType,
      area: edu.area,
      endDate: edu.endDate ? new Date(edu.endDate).getFullYear().toString() : "",
      courses: edu.courses || [],
    })),

    skills: flatSkills,
    interests: flatInterests,
    projects: jsonResume.projects || [],
  };
};

const emptyResume: ResumeData = {
  basics: {
    name: "",
    label: "",
    summary: "",
  },
  work: [],
  education: [],
  skills: [],
  interests: [],
  projects: [],
};

const useResumeData = () => {
  const [resume, setResume] = useState<ResumeData>(emptyResume);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/resume.json")
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        return response.json() as Promise<JsonResume>;
      })
      .then((jsonResume) => {
        setResume(transformJsonResume(jsonResume));
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Unknown error";
        console.error("Failed to load resume data:", err);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return { resume, loading, error };
};

export { useResumeData };
